import React, { useState, useEffect } from 'react';
import Loading from '../Common/Loading';

interface LessonResult {
    lesson_code: string;
    lesson_name: string;
    has_tutorial: boolean;
    is_uni: boolean;
    questions: string[];
}

const SearchQnTypes: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK || 'http://127.0.0.1:8000/';
    
    // --- State Management ---
    const [country, setCountry] = useState('Australia');
    const [maths, setMaths] = useState(false);
    const [english, setEnglish] = useState(false);
    const [qnType, setQnType] = useState('O');
    const [isGenType, setIsGenType] = useState(false);
    
    const [searchIn, setSearchIn] = useState('Whole Lesson');
    const [pattern, setPattern] = useState('');
    const [matchType, setMatchType] = useState('Exact match');

    // Track active search mode ('qntype' vs 'text') to render appropriate layouts
    const [activeSearchType, setActiveSearchType] = useState<'qntype' | 'text'>('qntype');
    const [results, setResults] = useState<LessonResult[]>([]);
    const [tutorialCount, setTutorialCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const styles = {
        body: { fontFamily: 'Arial, sans-serif', fontSize: '13px', margin: 0, backgroundColor: '#fff', minHeight: '100vh', color: '#000' },
        navigator: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#7a96df', height: '50px', padding: '0 15px', color: 'white', fontSize: '18px' },
        navLink: { color: 'white', textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' },
        container: { padding: '15px' },
        sectionTitle: { fontSize: '14px', fontWeight: 'bold', margin: '15px 0 5px 0' },
        row: { display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '15px' },
        label: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', marginTop:'0' },
        inputElement: { marginRight: '5px', marginTop:"0" },
        cssTextBox: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', height: '22px' },
        cssButton: { fontFamily: 'Arial', fontSize: '13px', padding: '2px 10px', cursor: 'pointer', marginTop: '5px' },
        summaryText: { fontSize: '13px', fontWeight: 'bold', marginTop: '20px', marginBottom: '5px' },
        hr: { border: '0', borderBottom: '1px solid #ccc', margin: '0 0 10px 0' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px', border: '1px solid #ccc' },
        th: { background: '#f2f2f2', color: '#333', padding: '8px', border: '1px solid #ccc', fontWeight: '600', textAlign: 'center' as const },
        tdLeft: { border: '1px solid #ccc', padding: '6px', width: '35%', textAlign: 'left' as const, textDecoration: 'underline',},
        tdMiddle: { border: '1px solid #ccc', padding: '6px', textAlign: 'left' as const },
        tdRight: { border: '1px solid #ccc', padding: '6px', width: '8%', textAlign: 'center' as const },
        link: { textDecoration: 'underline', color: '#0073e6', cursor: 'pointer' },
        lessonLink: { textDecoration: 'none', color: '#000', cursor: 'pointer', fontWeight: '500' }
    };

    // --- Search Trigger ---
    const handleSearch = async (searchMode: 'qntype' | 'text') => {
        setLoading(true);
        setHasSearched(true);
        setActiveSearchType(searchMode);
        setResults([]);

        try {
            const res = await fetch(`${API_BASE_URL}api/search_qntypes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search_type: searchMode,
                    country: country,
                    qn_type: qnType,
                    is_gen_type: isGenType,
                    maths: maths,
                    english: english,
                    search_in: searchIn,
                    pattern: pattern,
                    match_type: matchType
                })
            });
            const json = await res.json();
            
            if (json.error) {
                alert(`Error: ${json.error}`);
            } else {
                setResults(json.results || []);
                setTutorialCount(json.tutorial_count || 0);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- Navigation Links Handlers ---
    const openQuestionViewer = (qnNo: string, lessonCode: string) => {
        let url = `/LessonBrowser/QuestionViewer?QnNo=${qnNo}&Title=${lessonCode}&Country=${country}&from=SearchQnTypes`;
        
        // If it's textual pattern matching search, forward text search tokens down to viewer
        if (activeSearchType === 'text') {
            url += `&match=${matchType}&SearchWords=${encodeURIComponent(pattern)}`;
        }

        const w = 1200; const h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);
        window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    };

    const openLessonViewer = (lessonCode: string) => {
        const url = `/LessonBrowser/LessonViewer?Lessonid=${lessonCode}&Country=${country}&from=SearchQnTypes`;
        const w = 1200; const h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);
        window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    };

    const openTutorialViewer = (lessonCode: string) => {
        const url = `/LessonBrowser/Tutorial_PrincipleContents?Title=${lessonCode}&Country=${country}&tp=tut&match=${matchType}&SearchWords=${encodeURIComponent(pattern)}`;
        const w = 1200; const h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);
        window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    };

    const qnTypes = [
        { val: "Selective", text: "Select..." },
        { val: "O", text: "Objective (O)" },
        { val: "M", text: "Mix & Match (M)" },
        { val: "B", text: "True or False (B)" },
        { val: "W", text: "Word (W)" },
        { val: "F", text: "Fill in the Blank(s) (F)" },
        { val: "N", text: "NumberWiz (N)" },
        { val: "c", text: "Multi-choice (C)" },
        { val: "l", text: "Orderedlist (L)" },
        { val: "I", text: "Identify (I)" },
        { val: "D", text: "Multi-Selection Boxes (D)" }
    ];

    const totalLessons = results.length;
    const totalQuestions = results.reduce((sum, grp) => sum + grp.questions.length, 0);

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <div style={styles.navigator}>
                <a href="/LessonBrowser/LessonBrowser" style={{...styles.navLink, fontSize:"18px"}}>Back</a>
                <div style={{ textAlign: 'center', flexGrow: 1, fontSize:"24px"}}>Question Navigator</div>
                <a href="/" style={{...styles.navLink, fontSize:"18px"}}>Logout</a>
            </div>

            <div style={styles.container}>
                {/* --- Section 1: Country & Subject --- */}
                <div style={styles.sectionTitle}>Select Country</div>
                <div style={styles.row}>
                    <label style={styles.label}>
                        <input type="radio" name="country" value="Australia" checked={country === 'Australia'} onChange={(e) => setCountry(e.target.value)} style={{...styles.inputElement, marginLeft:"0"}} /> Australia
                    </label>
                    <label style={styles.label}>
                        <input type="radio" name="country" value="Canada" checked={country === 'Canada'} onChange={(e) => setCountry(e.target.value)} style={styles.inputElement} /> North America
                    </label>
                </div>
                <div style={styles.row}>
                    <label style={styles.label}>
                        <input type="checkbox" checked={maths} onChange={(e) => setMaths(e.target.checked)} style={{...styles.inputElement, marginBottom:"0", marginLeft:"0"}} /> Maths
                    </label>
                    <label style={styles.label}>
                        <input type="checkbox" checked={english} onChange={(e) => setEnglish(e.target.checked)} style={{...styles.inputElement, marginBottom:"0"}} /> English
                    </label>
                </div>

                {/* --- Section 2: QnTypes Search Block --- */}
                <div style={styles.sectionTitle}>Search for Qntypes...</div>
                <div style={{ ...styles.row, gap: '10px' }}>
                    <select style={styles.cssTextBox} value={qnType} onChange={(e) => setQnType(e.target.value)}>
                        {qnTypes.map(t => (
                            <option key={t.val} value={t.val}>{t.text}</option>
                        ))}
                    </select>
                    <label style={styles.label}>
                        Search for Generative type of Questions.... 
                        <input type="checkbox" checked={isGenType} onChange={(e) => setIsGenType(e.target.checked)} style={{ marginLeft: '5px' }} />
                    </label>
                </div>
                <button style={styles.cssButton} onClick={() => handleSearch('qntype')}>Search</button>

                {/* --- Section 3: Text Search Block --- */}
                <div style={styles.sectionTitle}>Search for text in...</div>
                <div style={styles.row}>
                    <label style={styles.label}>
                        <input type="radio" name="searchIn" value="Whole Lesson" checked={searchIn === 'Whole Lesson'} onChange={(e) => setSearchIn(e.target.value)} style={{...styles.inputElement, marginLeft:"0"}} /> Whole Lesson
                    </label>
                    <label style={styles.label}>
                        <input type="radio" name="searchIn" value="Tutorial" checked={searchIn === 'Tutorial'} onChange={(e) => setSearchIn(e.target.value)} style={styles.inputElement} /> Tutorial
                    </label>
                    <label style={styles.label}>
                        <input type="radio" name="searchIn" value="Questions" checked={searchIn === 'Questions'} onChange={(e) => setSearchIn(e.target.value)} style={styles.inputElement} /> Questions
                    </label>
                </div>

                <div style={styles.row}>
                    <span style={{ fontSize: '13px' }}>Pattern:</span>
                    <input type="text" style={{ ...styles.cssTextBox, width: '200px' }} value={pattern} onChange={(e) => setPattern(e.target.value)} />
                    
                    <label style={{ ...styles.label, marginLeft: '10px' }}>
                        <input type="radio" name="matchType" value="Exact match" checked={matchType === 'Exact match'} onChange={(e) => setMatchType(e.target.value)} style={styles.inputElement} /> Exact match
                    </label>
                    <label style={styles.label}>
                        <input type="radio" name="matchType" value="Partial match" checked={matchType === 'Partial match'} onChange={(e) => setMatchType(e.target.value)} style={styles.inputElement} /> Partial match
                    </label>
                </div>
                <button style={styles.cssButton} onClick={() => handleSearch('text')}>Search</button>

                {/* --- Section 4: Results Area --- */}
                {hasSearched && (
                    <>
                        <div style={styles.summaryText}>
                            Found: {totalLessons} Lessons | {totalQuestions} Questions {activeSearchType === 'text' && `| ${tutorialCount} Tutorials`}
                        </div>
                        <hr style={styles.hr} />

                        {results.length === 0 && !loading && (
                            <div style={{ padding: '10px', color: '#666' }}>No records found.</div>
                        )}

                        {results.length > 0 && (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Lesson Name</th>
                                        {activeSearchType === 'text' && <th style={styles.th}>Tutorial</th>}
                                        <th style={styles.th}>Questions</th>
                                        <th style={styles.th}>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((grp) => (
                                        <tr key={grp.lesson_code}>
                                            {/* Left Col: Lesson Name/Code (Launches LessonViewer) */}
                                            <td style={styles.tdLeft}>
                                                <span style={styles.lessonLink} onClick={() => openLessonViewer(grp.lesson_code)}>
                                                    {grp.lesson_name} ({grp.lesson_code}) {grp.is_uni && <span style={{ color: 'orange' }}> _Uni </span>}
                                                </span>
                                            </td>
                                            
                                            {/* Optional Tutorial Column for Text Search */}
                                            {activeSearchType === 'text' && (
                                                <td style={{ ...styles.tdMiddle, width: '12%', textAlign: 'center' }}>
                                                    {grp.has_tutorial ? (
                                                        <span style={{ ...styles.link, color: '#28a745', fontWeight: '600' }} onClick={() => openTutorialViewer(grp.lesson_code)}>
                                                            Tutorial
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                            )}

                                            {/* Middle Col: Question Links */}
                                            <td style={styles.tdMiddle}>
                                                {grp.questions.length > 0 ? grp.questions.map((qn, idx) => (
                                                    <React.Fragment key={qn}>
                                                        <span style={styles.link} onClick={() => openQuestionViewer(qn, grp.lesson_code)}>
                                                            {qn}
                                                        </span>
                                                        {idx < grp.questions.length - 1 && ', '}
                                                    </React.Fragment>
                                                )) : '-'}
                                            </td>
                                            
                                            {/* Right Col: Combined Count */}
                                            <td style={styles.tdRight}>
                                                {activeSearchType === 'text' 
                                                    ? (grp.questions.length + (grp.has_tutorial ? 1 : 0)) 
                                                    : grp.questions.length
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchQnTypes;